import { useEffect, useMemo, useState } from 'react'
import {
    TITLE_BREAKDOWN_PROCESSING,
    TITLE_BREAKDOWN_STATUSES,
    PURCHASE_TYPE_AUTO_CHAIN,
    MAIN_TAB,
    TAB_TYPE_LEASE,
} from '@/constants'
import { Link, useParams } from 'react-router-dom'
import * as actions from '@actions'
import * as store from '@store'
import { useWireState, useWireValue } from '@forminator/react-wire'
import CircleSpinner from '@/components/shared/CircleSpinner'
import { createSyntheticDownload } from '@/utils'
import { toast } from 'react-toastify'
import { useSearchParams } from 'react-router-dom'
import usePayments from '@/hook/usePayments'
import { useNavigate } from 'react-router-dom'
import { FaGears } from 'react-icons/fa6'
import { ConveyanceTable } from '../Chaining/ConveyanceTable/ConveyanceTable'
import { getRunsheetDocsByTab } from '../Runsheet/Runsheet.lib'
import { usePollLeaseholdChainStatus } from '@/hook/usePollLeaseholdChainStatus'
import { useRealtimeLeaseholdChainStatus } from '@/hook/useRealtimeLeaseholdChainStatus'

export const LeaseholdChain = () => {
    
    const navigate = useNavigate()
    const { id: runsheetId } = useParams()
    const [searchParams] = useSearchParams()
    const [leaseholdChain, setLeaseholdChain] = useWireState(store.leaseholdChain)
    const tabs = useWireValue(store.runsheetTabs)
    const runsheet = useWireValue(store.currentRunsheet)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const [hasRecentFailure, setHasRecentFailure] = useState(false)
    const [exportLoading, setExportLoading] = useState(false)
    const [hasMissingData, setHasMissingData] = useState(false)
    const payments = usePayments()
    
    const activeTab = useMemo(() => searchParams.get('activeTab') || MAIN_TAB, [searchParams])
    const currentTab = useMemo(() => tabs.find(t => t.id === activeTab) || tabs[0], [tabs, activeTab])
    
    const runsheetDocs = useMemo(() => getRunsheetDocsByTab(currentTab, runsheet), [runsheet, currentTab])
    
    const processLeaseholdChain = async (numDocuments, remainingBalance, fundingSource) => {
        setHasRecentFailure(false)
        await actions.processLeaseholdChain(runsheetId, numDocuments, activeTab, fundingSource)
        
        if (!remoteConfig?.realtimeEnabled) await actions.updateLocalCredits()
    }
    
    const handleLeaseholdChain = async () => {
        if (!payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_AUTO_CHAIN,
            itemCount: runsheetDocs.length,
            onConfirmPayment:
                (remainingBalance, fundingSource) =>
                    processLeaseholdChain(runsheetDocs.length, remainingBalance, fundingSource),
        })) {
            await processLeaseholdChain(runsheetDocs.length)
        }
    }
    
    const exportLeaseholdChain = async () => {
        setExportLoading(true)
        
        const res = await actions.exportLeaseholdChain(runsheetId, activeTab)
        
        if (res)
            createSyntheticDownload(res.data, res.headers['content-type'], res.headers['content-name'])
        else
            toast.error('Failed to export Leasehold Chain List, please try again.')
        
        setExportLoading(false)
    }
    
    const retryChaining = () => actions.retryLeaseholdChain(runsheetId, activeTab)
    
    useRealtimeLeaseholdChainStatus(leaseholdChain?.id, runsheetId, activeTab, setHasRecentFailure)
    
    usePollLeaseholdChainStatus(
        TITLE_BREAKDOWN_PROCESSING.includes(leaseholdChain?.status) ? runsheetId : null,
        activeTab,
        setHasRecentFailure,
    )
    
    const fetchChainData = async () => {
        
        const runsheet = await actions.fetchRunsheetById(runsheetId)
        const tabs = await actions.getTabsFromRunsheet(runsheetId)
        
        if (store.remoteConfig.getValue().chainer?.validateMissingData) {
            
            const tab = tabs.find(t => t.id === activeTab)
            const docs = getRunsheetDocsByTab(tab, runsheet)
            
            const missingData = docs.some(r =>
                !r.value.instrumentDate || !r.value.grantors.length || !r.value.grantees.length)
            
            setHasMissingData(missingData)
            
            if (missingData) return
            
        }
        
        const data = await actions.fetchLeaseholdChain(runsheetId, activeTab)
        
        if (data) {
            setLeaseholdChain(data)
            setHasRecentFailure(data.hasRecentFailure)
            
            await actions.listUniqueParties(runsheetId)
        } else {
            navigate('/runsheets')
        }
    }
    
    useEffect(() => {
        setLeaseholdChain(null)
        fetchChainData()
        
    }, [activeTab])
    
    const chainingBreadcrumb = () =>
        <div className="text-sm breadcrumbs ml-4 pt-0">
            <ul>
                <li><Link to="/collections">Home</Link></li>
                <li><Link to="/runsheets">Runsheets</Link></li>
                <li><Link to={`/runsheet/${runsheet?.id}?activeTab=${activeTab}`}>{runsheet?.name ?? ''}
                    {activeTab !== MAIN_TAB ? ` (${currentTab.name})` : ''}</Link></li>
                <li>Leasehold Chain</li>
            </ul>
        </div>
    
    if (TITLE_BREAKDOWN_STATUSES.PROCESSING === leaseholdChain?.status)
        return (
            <div className="flex flex-col w-full">
                {chainingBreadcrumb()}
                <div className="card bg-base-100 w-96 shadow-sm mx-auto mt-10">
                    <figure>
                        <FaGears className="text-5xl" />
                    </figure>
                    <div className="card-body">
                        <h2 className="card-title">Leasehold Chain Processing</h2>
                        <p>The leasehold chain is processing. It should be ready soon.</p>
                        <div className="mx-auto"><CircleSpinner /></div>
                    </div>
                </div>
            </div>
        )
    
    if (TITLE_BREAKDOWN_STATUSES.MANUAL_PROCESSING === leaseholdChain?.status)
        return (
            <div className="flex flex-col">
                {chainingBreadcrumb()}
                <div className="flex flex-col items-center">
                    <div className="text-2xl my-5 mt-10">The Leasehold Chain is loading. It should be ready soon.</div>
                    <CircleSpinner />
                </div>
            </div>
        )
    
    if (TITLE_BREAKDOWN_STATUSES.MANUAL_FAILED === leaseholdChain?.status)
        return (
            <div className="flex flex-col">
                {chainingBreadcrumb()}
                <div className="flex flex-col items-center">
                    <div className="text-2xl mt-10">
                        We're sorry for the inconvenience, but the Leasehold Chain failed to complete.
                    </div>
                    <div className="text-2xl my-5 mt-2">
                        Please retry and if the problem persists let us know and we'll be happy to help.
                    </div>
                    <div className="text-2xl my-5">
                        <button className="btn btn-primary" onClick={retryChaining}>Retry</button>
                    </div>
                </div>
            </div>
        )
    
    return (
        <div className="flex flex-col">
            
            <ConveyanceTable
                chain={leaseholdChain}
                setChain={setLeaseholdChain}
                conveyanceType={TAB_TYPE_LEASE}
                runsheetDocs={runsheetDocs}
                activeTab={activeTab}
                hasMissingData={hasMissingData}
                hasRecentFailure={hasRecentFailure}
                setHasRecentFailure={setHasRecentFailure}
                handleChain={handleLeaseholdChain}
                exportChain={exportLeaseholdChain}
                exportLoading={exportLoading}
                chainingBreadcrumb={chainingBreadcrumb}/>
        
        </div>
    )
}
